<template>
  <div data-component-group="business-form" data-component="google-advertisers">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <v-autocomplete
        v-model="advertiser"
        :items="advertisers"
        :loading="isLoadingAdvertisers"
        :label="$t('advertisers')"
        item-title="name"
        item-value="id"
        :return-object="true"
        :readonly="isDisabled"
        required
        v-model:search="advertiserSearch"
        :error-messages="advertiserErrors"
        @blur="v$.advertiser.$touch()"
      />

      <div class="component-actions">
        <Can action="update" subject="business">
          <v-btn color="secondary" @click="onUpdate">
            <v-icon>edit</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </Can>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasItems"
        :items="businessAdvertisers"
        :is-disabled="isDisabled || !$ability.can('update', 'business')"
        :on-remove="onRemoveAdvertiser"
      >
        <template v-slot:item="scope" >
          <div class="d-flex align-center">
            <v-icon> public </v-icon>
            {{ scope.item.name }}
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import Removables from '../../lists/Removables';
  import { isStr, isNum, debounce } from '../../../utils';

  const advertiserSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        advertiser: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Add',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        advertiser: null,
        advertisers: [],
        advertiserSearch: '',
        isLoadingAdvertisers: false,
        isRefreshingAdvertiser: false,
      };
    },
    computed: {
      businessAdvertisers() {
        // activeItem or its businessAdvertisers can be missing; fall back to []
        // so hasItems() and the list rendering don't read .length of undefined.
        return this.$store.getters['businesses/activeItem']?.businessAdvertisers ?? [];
      },
      hasItems() {
        return this.businessAdvertisers.length > 0;
      },
      advertiserErrors() {
        return this.v$.advertiser.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Google Advertiser is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id'(nextId, prevId) {
        if (nextId && prevId !== undefined) {
          this.clear();
        }
      },
      advertiser() {
        this.isRefreshingAdvertiser = true;
      },
      advertiserSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingAdvertiser) {
            this.isRefreshingAdvertiser = false;
            return false;
          }

          advertiserSearchDebouncer(() => this.getAdvertisers(nextSearch));
        }
      },
    },
    methods: {
      getAdvertisers(search) {
        const query = {
          search,
        };

        this.isLoadingAdvertisers = true;

        return this.$store
          .dispatch('promotions/getAdvertisers', query)
          .then(({ advertisers }) => {
            this.advertisers = advertisers;
            this.isLoadingAdvertisers = false;
            return advertisers;
          })
          .catch((err) => {
            this.isLoadingAdvertisers = false;
            return err;
          });
      },
      getBusinessAdvertisers({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getBusinessAdvertisers', query);
      },
      canSubmitRequest() {
        return isNum(this.business.id) && isNum(this.advertiser.id);
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.onSubmit(this.advertiser);
      },
      onRemoveAdvertiser(item) {
        const query = {
          id: this.business.id,
          advertiser: item,
        };

        return this.$store.dispatch('businesses/removeBusinessAdvertiser', query);
      },
      clear() {
        this.v$.$reset();
        this.advertiser = null;
        this.advertisers = [];
        this.advertiserSearch = '';
        this.isLoadingAdvertisers = false;
        this.isRefreshingAdvertiser = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='google-advertisers'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    padding: 0 0 16px 0;
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
